import React, { useMemo, useState } from "react";
import { DocumentUploadFieldAccountDocumentType } from "common/services/formBuilderService";
import {
  AI_ASSISTANT_FIELD_TYPES,
  type FormStructure,
} from "common/utils/formStructure";
import type { GetSubmissionTypeForBuilderQuery } from "../../../../../generated/graphql";
import type { AuthContextType } from "../../../../Authorization/types";
import { AssistantSuggestion } from "../../../../Submissions/ChatAgent/AssistantIntro";
import ChatPanel from "../../../../Submissions/ChatAgent/ChatPanel";
import ChatToggleButton from "../../../../Submissions/ChatAgent/ChatToggleButton";
import {
  BUILDER_THINKING_PHRASES,
  BUILDER_WORKING_PHRASES,
} from "../../../../Submissions/ChatAgent/ThinkingIndicator";
import BuilderToolActivityCard from "../../../../Submissions/ChatAgent/toolCards/BuilderToolActivityCard";
import { useBuilderFormContext } from "./form/form";

const BUILDER_SUGGESTIONS: AssistantSuggestion[] = [
  {
    text: "Draft a new damage assessment record with grouped sections for structure, utilities, and safety notes.",
    iconName: "component",
  },
  {
    text: "Add photo and document upload fields to capture site evidence for this form.",
    iconName: "upload",
  },
  {
    text: "Create dropdowns with standard compliance statuses and required follow-up actions.",
    iconName: "list",
  },
];

const prepareDocumentTypesForAI = (
  account: AuthContextType["account"],
): DocumentUploadFieldAccountDocumentType[] => {
  if (!account?.accountDocumentTypes) {
    return [];
  }

  return account.accountDocumentTypes.map(adt => ({
    id: adt.id,
    name: adt.name,
    systemType: adt.documentType?.name,
    allowedMimeTypes: adt.allowedMimeTypes,
  }));
};

export const useSubmissionBuilderV2ChatAgent = ({
  account,
  existingSubmissionType,
}: {
  account: AuthContextType["account"];
  existingSubmissionType: GetSubmissionTypeForBuilderQuery["submissionType"];
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const ToggleButton = useMemo(() => {
    return (
      <ChatToggleButton onClick={() => setIsOpen(!isOpen)} isOpen={isOpen} />
    );
  }, [isOpen]);

  const { setValue, watch, trigger } = useBuilderFormContext();

  const capabilities = useMemo(
    () => ({
      types: AI_ASSISTANT_FIELD_TYPES,
      account: {
        userGroups:
          account?.userGroups?.map(({ id, name }) => ({ id, name })) ?? [],
        documentTypes: prepareDocumentTypesForAI(account),
      },
      submissionType: {
        id: existingSubmissionType?.id,
      },
      defaultPermissions: {
        canCreateRolesAndGroupIds: watch("canCreateRolesAndGroupIds") ?? [],
        hiddenFromPublicByDefault: watch("hiddenFromPublicByDefault") ?? true,
      },
    }),
    [account, existingSubmissionType, watch],
  );

  const watchedFormStructure = watch("formStructure");

  const formStructureWithCapabilities: FormStructure & {
    __capabilities?: any;
  } = useMemo(
    () => ({
      ...(watchedFormStructure ?? { schema: {}, uiSchema: {} }),
      __capabilities: capabilities,
    }),
    [watchedFormStructure, capabilities],
  );

  const Panel = useMemo(() => {
    return (
      <ChatPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        submissionTypeVersionId={undefined}
        formStructure={formStructureWithCapabilities}
        api="/api/ai/chat/submissions-builder/stream"
        suggestions={BUILDER_SUGGESTIONS}
        showModelPicker
        ToolActivityCardComponent={BuilderToolActivityCard}
        thinkingPhrases={BUILDER_THINKING_PHRASES}
        workingPhrases={BUILDER_WORKING_PHRASES}
        onFormUpdate={(out: any) => {
          const next = out?.formStructure;
          if (!next) return;

          if (next.schema && next.uiSchema) {
            setValue("formStructure", next, { shouldDirty: true });
          }

          void trigger();
        }}
      />
    );
  }, [isOpen, formStructureWithCapabilities, setValue, trigger]);

  return {
    isOpen,
    setIsOpen,
    ChatToggleButton: ToggleButton,
    ChatPanel: Panel,
    isChatPanelOpen: isOpen,
  } as const;
};

export default useSubmissionBuilderV2ChatAgent;
